import { ColDef } from "ag-grid-community";
import { useEffect, useMemo, useRef, useState } from "react";

import AgGridReactTable from "@components/ag-grid/AgGridReactTable";
import AgGridSkeleton from "@components/ag-grid/AgGridSkeleton";
import CustomButton from "@components/buttons/CustomButton";
import GlobalTextInputField from "@components/inputs/GlobalTextInputField";
import { primeServices } from "@services/apis/Prime";
import {
  GetRampRatesData,
  GetRampRatesResponse,
} from "@services/models/Prime/GetRampRatesModel";
import ConstantFunctions from "src/utils/constants_functions";
import useSnackbarMessage from "../../../utils/HandleSuccessErrorSnackbars";

const Rates = () => {
  const [payinRate, setPayinRate] = useState<string>("");
  const [payoutRate, setPayoutRate] = useState<string>("");
  const [p2pCryptoCurrencyId, setP2pCryptoCurrencyId] = useState<string>("");
  const [fiatCurrencyId, setFiatCurrencyId] = useState<string>("");
  const [merchantId, setMerchantId] = useState<string>("");
  const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false);
  const [rampRatesLoading, setRampRatesLoading] = useState<boolean>(false);
  const [rampRates, setRampRates] = useState<GetRampRatesData[]>([]);
  const [rampRatesError, setRampRatesError] = useState<string>("");

  const gridRef = useRef<any>(null);
  const constantFunctions = new ConstantFunctions();
  const { handleError, handleSuccess } = useSnackbarMessage();

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        headerName: "Date",
        field: "updatedAt",
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        flex: 1,
        minWidth: 100,
        maxWidth: 140,
        filter: "agDateColumnFilter",
        cellRenderer: (props: any) => {
          const { data } = props;
          return constantFunctions.formatDate(data.updatedAt);
        },
      },
      {
        headerName: "Time",
        field: "updatedAt",
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        flex: 0.8,
        minWidth: 80,
        maxWidth: 120,
        filter: "agTextColumnFilter",
        cellRenderer: (props: any) => {
          const { data } = props;
          return constantFunctions.timeFormatInHHMMSS(data.updatedAt);
        },
      },
      {
        headerName: "Token",
        field: "cryptoCurrencySymbol",
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        flex: 0.8,
        minWidth: 80,
        maxWidth: 120,
        filter: "agTextColumnFilter",
        cellRenderer: (props: any) => {
          const { data } = props;
          return (
            <div className="flex flex-row items-center gap-[4px]">
              <img
                src={data.cryptoCurrencyImage}
                alt={data.cryptoCurrencySymbol}
                className="inline-block w-3 h-3"
              />
              <span className="text-[10px] text-text-base font-medium">
                {data.cryptoCurrencySymbol || "-"}
              </span>
            </div>
          );
        },
      },
      {
        headerName: "Fiat",
        field: "fiatCurrencySymbol",
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        flex: 0.8,
        minWidth: 80,
        maxWidth: 120,
        filter: "agTextColumnFilter",
        cellRenderer: (props: any) => {
          const { data } = props;
          return (
            <div className="flex flex-row items-center gap-[4px]">
              <img
                src={data.fiatCurrencyImage}
                alt={data.fiatCurrencySymbol}
                className="inline-block w-3 h-3"
              />
              <span className="text-[10px] text-text-base font-medium">
                {data.fiatCurrencySymbol || "-"}
              </span>
            </div>
          );
        },
      },
      {
        headerName: "Payin Rate",
        field: "buy_rate",
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        flex: 1,
        minWidth: 100,
        maxWidth: 140,
        filter: "agNumberColumnFilter",
        cellRenderer: (props: any) => {
          const { data } = props;
          return data.buy_rate || "-";
        },
      },
      {
        headerName: "Payout Rate",
        field: "sell_rate",
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        flex: 1,
        minWidth: 100,
        maxWidth: 140,
        filter: "agNumberColumnFilter",
        cellRenderer: (props: any) => {
          const { data } = props;
          return data.sell_rate || "-";
        },
      },
      {
        headerName: "Merchant Name",
        field: "merchantName",
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        flex: 1.2,
        minWidth: 120,
        maxWidth: 180,
        filter: "agTextColumnFilter",
        cellRenderer: (props: any) => {
          const { data } = props;
          return data.merchantName || "-";
        },
        wrapText: true,
        autoHeight: true,
        cellStyle: {
          whiteSpace: "normal",
          wordBreak: "break-word",
          lineHeight: "1.2",
        },
      },
    ],
    [constantFunctions]
  );

  const handleSubmit = async () => {
    try {
      setIsButtonLoading(true);
      await primeServices.setRates({
        buyRate: Number(payinRate),
        sellRate: Number(payoutRate),
        fiatCurrencyId: Number(fiatCurrencyId),
        p2pCryptoCurrencyId: Number(p2pCryptoCurrencyId),
        merchantId: merchantId ? Number(merchantId) : undefined,
      });
      handleSuccess({ message: "Rates Updated Successfully" });
      getRampRates();
    } catch (Exception) {
      handleError({ message: "Failed to update rates" });
    } finally {
      setIsButtonLoading(false);
    }
  };

  const getRampRates = async () => {
    try {
      setRampRatesLoading(true);
      const response: GetRampRatesResponse = await primeServices.getRampRates();
      setRampRates(response.data);
    } catch (error: any) {
      setRampRatesError(error.message || "Failed to fetch prime orders.");
    } finally {
      setRampRatesLoading(false);
    }
  };

  useEffect(() => {
    getRampRates();
  }, []);

  return (
    <>
      <div className="h-[70px] border-b border-border-subtle bg-bg-page-body flex flex-row sticky top-0 bg-white z-10 pl-[20px]">
        <p className="flex text-text-base text-[14px] font-semibold items-center">
          Rates
        </p>
      </div>

      <div className="flex flex-row items-start justify-between">
        <div
          className="flex flex-col bg-bg-page-body py-[20px] px-[12px] overflow-hidden w-[62%]"
          style={{ height: "calc(100dvh - 70px)" }}
        >
          {/* Table */}
          {rampRatesLoading && rampRates.length === 0 ? (
            <AgGridSkeleton
              columnDefs={columnDefs}
              style={{ height: "100%", width: "100%" }}
            />
          ) : (
            <AgGridReactTable
              ref={gridRef}
              rowData={rampRates}
              columnDefs={columnDefs}
              getRowId={(params) =>
                `${params.data.cryptoCurrencySymbol}-${params.data.fiatCurrencySymbol}-${params.data.updatedAt}`
              }
              error={rampRatesError}
            />
          )}
        </div>
        <div className="flex flex-col gap-[8px] bg-bg-base border border-border-subtle rounded-lg p-[12px] w-1/3 my-[20px] mx-[12px]">
          <div className="flex flex-row items-center justify-between gap-[28px]">
            <p className="text-text-base text-[12px] font-normal">
              Payin Rate*
            </p>
            <GlobalTextInputField
              type="number"
              value={payinRate}
              onChange={(e) => setPayinRate(e.target.value)}
              placeholder="Enter Payin Rate"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div className="flex flex-row items-center justify-between gap-[28px]">
            <p className="text-text-base text-[12px] font-normal">
              Payout Rate*
            </p>
            <GlobalTextInputField
              type="number"
              value={payoutRate}
              onChange={(e) => setPayoutRate(e.target.value)}
              placeholder="Enter Payout Rate"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div className="flex flex-row items-center justify-between gap-[28px]">
            <p className="text-text-base text-[12px] font-normal">
              P2P Crypto Currency ID*
            </p>
            <GlobalTextInputField
              type="number"
              value={p2pCryptoCurrencyId}
              onChange={(e) => setP2pCryptoCurrencyId(e.target.value)}
              placeholder="Enter Crypto ID"
              allowDecimal={false}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div className="flex flex-row items-center justify-between gap-[28px]">
            <p className="text-text-base text-[12px] font-normal">
              Fiat Currency ID*
            </p>
            <GlobalTextInputField
              type="number"
              value={fiatCurrencyId}
              onChange={(e) => setFiatCurrencyId(e.target.value)}
              placeholder="Enter Fiat ID"
              allowDecimal={false}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div className="flex flex-row items-center justify-between gap-[28px]">
            <p className="text-text-base text-[12px] font-normal">
              Merchant ID
            </p>
            <GlobalTextInputField
              type="number"
              value={merchantId}
              onChange={(e) => setMerchantId(e.target.value)}
              placeholder="Enter merchant ID"
              allowDecimal={false}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <CustomButton
            text="ADD RATE"
            isActive={
              payinRate !== "" &&
              payoutRate !== "" &&
              p2pCryptoCurrencyId !== "" &&
              fiatCurrencyId !== ""
            }
            onClick={handleSubmit}
            isButtonLoading={isButtonLoading}
          />
        </div>
      </div>
    </>
  );
};

export default Rates;
